import React, { useState, useEffect } from 'react';
import database from '../database';
import { parsePGN } from '../utils/pgnUtils';
import { trackFormSubmission, trackUIInteraction } from '../utils/gtm';

function AddLineModal({ currentOpeningId, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    openingId: currentOpeningId || '',
    name: '',
    pgn: '',
    description: ''
  });
  const [openings, setOpenings] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Load available openings
  useEffect(() => {
    const loadOpenings = async () => {
      try {
        const allOpenings = await database.getAllOpenings();
        setOpenings(allOpenings);
        
        // If no current opening, default to first available
        if (!currentOpeningId && allOpenings.length > 0) {
          setFormData(prev => ({ ...prev, openingId: allOpenings[0].id }));
        }
      } catch (error) {
        console.error('Failed to load openings:', error);
      }
    };
    loadOpenings();
  }, [currentOpeningId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Line name is required');
      trackFormSubmission('add_line', false, 'Line name is required');
      return;
    }
    
    if (!formData.pgn.trim()) {
      setError('PGN moves are required');
      trackFormSubmission('add_line', false, 'PGN moves are required');
      return;
    }

    if (!formData.openingId) {
      setError('Opening selection is required');
      trackFormSubmission('add_line', false, 'Opening selection is required');
      return;
    }

    // Validate PGN format
    try {
      const moves = parsePGN(formData.pgn);
      if (moves.length === 0) {
        setError('Invalid PGN format');
        trackFormSubmission('add_line', false, 'Invalid PGN format');
        return;
      }
    } catch (error) {
      setError('Invalid PGN format');
      trackFormSubmission('add_line', false, 'Invalid PGN format');
      return;
    }

    setIsSubmitting(true);
    setError('');
    
    try {
      await database.createLine({
        opening_id: parseInt(formData.openingId),
        name: formData.name,
        pgn: formData.pgn,
        information: formData.description || `Custom variation: ${formData.name}`
      });
      
      // Track successful form submission
      trackFormSubmission('add_line', true);
      
      onSuccess();
    } catch (error) {
      console.error('Failed to create line:', error);
      setError('Failed to create line. Please try again.');
      
      // Track form submission error
      trackFormSubmission('add_line', false, error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex justify-center items-center z-50">
      <div className="bg-gray-800 p-8 rounded-xl border border-gray-600 shadow-2xl max-w-lg w-full mx-4">
        <h3 className="text-2xl font-bold text-blue-400 mb-6 flex items-center">
          <span className="mr-2">➕</span>
          Add New Line
        </h3>
        
        <form onSubmit={handleSubmit}>
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Opening *
            </label>
            <select
              name="openingId"
              value={formData.openingId}
              onChange={handleInputChange}
              className="w-full p-3 rounded-md border border-gray-600 bg-gray-700 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              required
            >
              <option value="">Select an opening...</option>
              {openings.map(opening => (
                <option key={opening.id} value={opening.id}>
                  {opening.name} ({opening.player_color || 'white'})
                </option>
              ))}
            </select>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Line Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="e.g., Sicilian Dragon"
              className="w-full p-3 rounded-md border border-gray-600 bg-gray-700 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              required
            />
          </div>
          
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              PGN Moves *
            </label>
            <textarea
              name="pgn"
              value={formData.pgn}
              onChange={handleInputChange}
              placeholder="1. e4 c5 2. Nf3 d6 3. d4 cxd4 4. Nxd4 Nf6..."
              rows="4"
              className="w-full p-3 rounded-md border border-gray-600 bg-gray-700 text-white font-mono text-sm placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
              required
            />
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Optional description of this variation..."
              rows="2"
              className="w-full p-3 rounded-md border border-gray-600 bg-gray-700 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
            />
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-900/30 border border-red-500 rounded-md text-red-200 text-sm">
              {error}
            </div>
          )}
          
          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => {
                trackUIInteraction('modal', 'cancel', 'add_line');
                onClose();
              }}
              className="flex-1 py-3 px-4 rounded-md font-medium bg-gray-600 hover:bg-gray-700 text-white transition-all"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !formData.openingId || !formData.name || !formData.pgn}
              className={`flex-1 py-3 px-4 rounded-md font-medium transition-all ${
                isSubmitting || !formData.openingId || !formData.name || !formData.pgn
                  ? 'bg-gray-600 cursor-not-allowed text-gray-400'
                  : 'bg-green-600 hover:bg-green-700 text-white shadow-lg hover:shadow-xl'
              }`}
            >
              {isSubmitting ? 'Creating...' : '✅ Add Line'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddLineModal;